// Step 4 — Modules-picker (T14). 4 toggles met live totaalprijs.
//
// Wordt door `getStepSequence` overgeslagen bij partner-deeplink — daar
// dicteert het partner-pakket de modules en valt er niets te kiezen. Deze
// stap rendert dus alleen in non-partner-flows; pricing draait dan zonder
// partner-discount.
//
// Update Subscriptions-doc met nieuwe modules-shape + CompagnyMeta lastStep:5.

import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import firebase from '../../../firebase/compat'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { OnboardingFlow as OnboardingFlowContext } from '../../../StateManagment/OnboardingFlow'
import useDevPreview from '../../../hooks/useDevPreview'
import MODULES from '../../../helpers/subscriptionHelpers/moduleDefinitions'
import MODULE_DESCRIPTIONS from '../../../helpers/subscriptionHelpers/moduleDescriptions'
import calculateTotal from '../../../helpers/subscriptionHelpers/calculateTotal'
import Modal from '../../../components/common/Modal'

const MODULE_SLUGS = ['impactMeten', 'personas', 'housevisits', 'portfolio']

const Step4Modules = ({ onNext, onPrev }) => {

    // Context
    const {
        compagnyId,
        modules,
        setModules,
        lockedModules,
    } = useContext(OnboardingFlowContext)

    // Local form-state als kopie van context-modules. Wordt door de
    // orchestrator (OnboardingFlow.jsx) geseed met de partner-pakket-modules
    // én freeIncludedModules zodra de Partner-Firestore-fetch klaar is.
    //
    // Synchroniseer `draft` met `modules` tot de gebruiker een keuze maakt —
    // anders missen we de seed wanneer Step 4 mount vóór `applyPartnerContext`
    // gedraaid is (bv. directe nav naar /signup/4?partner=tosss).
    const [draft, setDraft] = useState({
        impactMeten: !!modules.impactMeten,
        personas: !!modules.personas,
        housevisits: !!modules.housevisits,
        portfolio: !!modules.portfolio,
    })
    const userTouchedRef = useRef(false)
    useEffect(() => {
        if (userTouchedRef.current) return
        setDraft({
            impactMeten: !!modules.impactMeten,
            personas: !!modules.personas,
            housevisits: !!modules.housevisits,
            portfolio: !!modules.portfolio,
        })
    }, [modules])

    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    // Info-modal: welke module's uitleg open is, of null
    const [infoSlug, setInfoSlug] = useState(null)

    const { active: devPreview } = useDevPreview()

    // Stap 4 is uitgesloten van partner-flows (zie getStepSequence). Pricing
    // hier kent dus géén partner-discount; calculateTotal valt terug op
    // module-prijzen.
    const monthlyTotal = useMemo(
        () => calculateTotal({ activeModules: draft }),
        [draft]
    )

    const isLocked = (slug) => Array.isArray(lockedModules) && lockedModules.includes(slug)

    const toggleModule = (slug) => {
        if (isLocked(slug)) return
        userTouchedRef.current = true
        setDraft((prev) => ({ ...prev, [slug]: !prev[slug] }))
    }

    const anyActive = MODULE_SLUGS.some((slug) => draft[slug])

    const submit = async (e) => {
        e.preventDefault()
        setError('')

        // Dev-preview: skip ALLE validatie + Firestore-writes.
        if (devPreview) {
            setModules(draft)
            onNext()
            return
        }

        if (!anyActive) {
            setError('Kies minstens één module om door te gaan.')
            return
        }

        if (!compagnyId) {
            setError('Geen actieve onboarding-sessie. Begin opnieuw bij Step 1.')
            return
        }

        setLoading(true)
        try {
            // Subscriptions is server-only volgens Firestore-rules — een
            // Cloud Function doet beide writes (Subscriptions.modules +
            // CompagnyMeta.OnboardingStatus) atomisch en met tenant-check.
            const updateModules = firebase.functions().httpsCallable('updateOnboardingModules')
            await updateModules({ data: {
                compagnyId,
                modules: draft,
                lastStep: 5,
            }})

            setModules(draft)
            onNext()
        } catch (err) {
            console.error('Step4Modules submit error', err)
            setError(err.message || 'Er ging iets mis. Probeer het opnieuw.')
            setLoading(false)
        }
    }

    return (
        <div className="onboarding-step">
            <h1>Welke modules wil je gebruiken?</h1>
            <p className="onboarding-step-intro">
                Combineer wat je nodig hebt. Je kunt later altijd modules toevoegen of opzeggen.
            </p>

            <div className="onboarding-modules-list">
                {MODULE_SLUGS.map((slug) => {
                    const mod = MODULES[slug]
                    const checked = draft[slug]
                    const desc = MODULE_DESCRIPTIONS[slug]
                    const locked = isLocked(slug)

                    return (
                        <div
                            key={slug}
                            className={`onboarding-module-row ${checked ? 'checked' : ''} ${locked ? 'locked' : ''}`}
                        >
                            <label className="onboarding-module-row-clickable">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleModule(slug)}
                                    disabled={locked}
                                />
                                <div className="onboarding-module-info">
                                    <h2>
                                        {mod.name}
                                        {locked && (
                                            <span className="onboarding-module-locked-tag">
                                                {checked ? 'Inbegrepen' : 'Vergrendeld'}
                                            </span>
                                        )}
                                    </h2>
                                    {desc && (
                                        <p className="onboarding-module-tagline">{desc.tagline}</p>
                                    )}
                                </div>
                            </label>
                            <button
                                type="button"
                                className="onboarding-module-info-button"
                                onClick={() => setInfoSlug(slug)}
                                aria-label={`Meer informatie over ${mod.name}`}
                            >
                                <InfoOutlinedIcon />
                            </button>
                            <div className="onboarding-module-price">
                                <span>€{mod.price}</span>
                                <span className="onboarding-module-price-unit">/mnd</span>
                            </div>
                        </div>
                    )
                })}
            </div>

            <div className="onboarding-modules-total">
                <span>Totaal per maand</span>
                <strong>€{monthlyTotal}</strong>
            </div>

            {error && <p className="onboarding-error">{error}</p>}

            <div className="onboarding-step-actions">
                <button
                    type="button"
                    className="onboarding-button-secondary"
                    onClick={onPrev}
                >
                    Terug
                </button>
                <button type="button" onClick={submit} disabled={loading}>
                    {loading ? <span className="onboarding-button-spinner" aria-label="Bezig" /> : 'Volgende'}
                </button>
            </div>

            <Modal
                modalOpen={!!infoSlug}
                setModalOpen={() => setInfoSlug(null)}
                pageHeader={infoSlug ? MODULES[infoSlug].name : ''}
                hideConfirm={true}
                cancelLabel="Sluiten"
                content={infoSlug && (
                    <div className="onboarding-module-info-modal">
                        <p className="onboarding-module-info-tagline">
                            {MODULE_DESCRIPTIONS[infoSlug].tagline}
                        </p>
                        <p>{MODULE_DESCRIPTIONS[infoSlug].body}</p>
                        <h3>Wanneer past dit?</h3>
                        <p>{MODULE_DESCRIPTIONS[infoSlug].bestFor}</p>
                        <p className="onboarding-module-info-price">
                            <strong>€{MODULES[infoSlug].price}</strong> per maand
                        </p>
                    </div>
                )}
            />
        </div>
    )

}

export default Step4Modules
